import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import StoriesBar from "@/components/StoriesBar";
import {
  Heart, MessageCircle, Share2, Image as ImageIcon, Video,
  Send, MoreHorizontal, Pin, Star, Trash2, X, ChevronDown,
  Globe, Twitter, Facebook, Linkedin, Copy, Clock, Users,
  Camera, Smile, ThumbsUp
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";

function PostCard({ post, user, onLike, onComment, onDelete, onFeature, onPin, onShare }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes_count || 0);
  const [shareOpen, setShareOpen] = useState(false);
  const isAdmin = user?.role === "super_admin" || user?.role === "master_admin" || user?.role === "developer" || user?.role === "admin";
  const isOwner = user?.user_id === post.user_id;

  useEffect(() => {
    if (user) {
      axios.get(`${API}/community/posts/${post.post_id}/liked`, { withCredentials: true })
        .then(res => setLiked(res.data.liked))
        .catch(() => {});
    }
  }, [post.post_id, user]);

  const handleLike = async () => {
    if (!user) { toast.error("Please log in to like posts"); return; }
    try {
      const res = await axios.post(`${API}/community/posts/${post.post_id}/like`, {}, { withCredentials: true });
      setLiked(res.data.liked);
      setLikeCount(prev => res.data.liked ? prev + 1 : prev - 1);
    } catch { toast.error("Failed to like"); }
  };

  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const res = await axios.get(`${API}/community/post/${post.post_id}`);
      setComments(res.data.comments || []);
    } catch { toast.error("Failed to load comments"); }
    finally { setLoadingComments(false); }
  };

  const handleToggleComments = () => {
    if (!showComments) loadComments();
    setShowComments(!showComments);
  };

  const handleComment = async () => {
    if (!user) { toast.error("Please log in to comment"); return; }
    if (!commentText.trim()) return;
    try {
      const res = await axios.post(`${API}/community/posts/${post.post_id}/comment`,
        { content: commentText }, { withCredentials: true });
      setComments(prev => [...prev, res.data.comment]);
      setCommentText("");
      onComment?.(post.post_id);
    } catch { toast.error("Failed to comment"); }
  };

  const handleShare = async (platform) => {
    const url = `${window.location.origin}/community?post=${post.post_id}`;
    const text = post.content?.substring(0, 140) || "Check out this post from Blue vs Blue!";
    
    await axios.post(`${API}/community/posts/${post.post_id}/share`, {}).catch(() => {});
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      copy: null
    };
    
    if (platform === "copy") {
      navigator.clipboard.writeText(url)
        .then(() => toast.success("Link copied!"))
        .catch(() => toast.error("Clipboard unavailable — copy manually"));
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], "_blank", "width=600,height=400");
    }
    setShareOpen(false);
  };

  const timeAgo = post.created_at ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) : "";
  const isBlogPost = post.post_type === "blog";

  return (
    <div className={`bg-white rounded-xl border ${post.is_pinned ? 'border-awareness-blue ring-1 ring-awareness-blue/20' : 'border-slate-200'} overflow-hidden`} data-testid={`community-post-${post.post_id}`}>
      {post.is_pinned && (
        <div className="bg-awareness-blue/10 px-4 py-1.5 flex items-center gap-1.5 text-awareness-blue text-xs font-semibold">
          <Pin className="w-3 h-3" /> Pinned Post
        </div>
      )}
      {post.is_featured && (
        <div className="bg-amber-50 px-4 py-1.5 flex items-center gap-1.5 text-amber-700 text-xs font-semibold">
          <Star className="w-3 h-3 fill-amber-500" /> Featured
        </div>
      )}

      {/* Post Header */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-deep-blue flex items-center justify-center text-white font-bold text-sm">
            {post.user_picture ? (
              <img src={post.user_picture} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              post.user_name?.charAt(0)?.toUpperCase() || "U"
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Link to={`/profile/${post.user_id}`} className="font-semibold text-deep-blue text-sm hover:underline">{post.user_name}</Link>
              {(post.user_role === "super_admin" || post.user_role === "admin") && (
                <span className="bg-deep-blue text-white text-[10px] px-1.5 py-0.5 rounded font-semibold">ADMIN</span>
              )}
              {isBlogPost && (
                <span className="bg-awareness-blue/10 text-awareness-blue text-[10px] px-1.5 py-0.5 rounded font-semibold">BLOG</span>
              )}
            </div>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {timeAgo}
            </span>
          </div>
        </div>
        
        {(isOwner || isAdmin) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`post-menu-${post.post_id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isAdmin && (
                <>
                  <DropdownMenuItem onClick={() => onFeature?.(post.post_id)}>
                    <Star className="w-4 h-4 mr-2" /> {post.is_featured ? "Unfeature" : "Feature"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onPin?.(post.post_id)}>
                    <Pin className="w-4 h-4 mr-2" /> {post.is_pinned ? "Unpin" : "Pin"}
                  </DropdownMenuItem>
                </>
              )}
              {(isOwner || isAdmin) && (
                <DropdownMenuItem className="text-red-500" onClick={() => onDelete?.(post.post_id)}>
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Content */}
      {post.content && (
        <div className="px-4 pb-3">
          <p className="text-slate-700 text-sm whitespace-pre-wrap leading-relaxed">{post.content}</p>
          {post.post_type === "live_stream" && post.stream_id && (
            <Link to={`/live/${post.stream_id}`} className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-colors">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              {post.content.includes("LIVE NOW") ? "Watch Live" : "Watch Replay"}
            </Link>
          )}
        </div>
      )}

      {/* Media */}
      {post.media?.length > 0 && (
        <div className="border-t border-b border-slate-100">
          {post.media.map(m => (
            <div key={m.media_id}>
              {m.media_type === "video" ? (
                <video src={`${API.replace('/api', '')}${m.url}`} controls className="w-full max-h-[500px] object-contain bg-black" />
              ) : (
                <img src={`${API.replace('/api', '')}${m.url}`} alt="" className="w-full max-h-[500px] object-contain bg-slate-50" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Stats bar */}
      <div className="px-4 py-2 flex items-center justify-between text-xs text-slate-400 border-b border-slate-100">
        <span>{likeCount > 0 ? `${likeCount} like${likeCount !== 1 ? 's' : ''}` : ''}</span>
        <div className="flex gap-3">
          {post.comments_count > 0 && <span>{post.comments_count} comment{post.comments_count !== 1 ? 's' : ''}</span>}
          {post.shares_count > 0 && <span>{post.shares_count} share{post.shares_count !== 1 ? 's' : ''}</span>}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-2 py-1 flex items-center border-b border-slate-100">
        <button onClick={handleLike} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${liked ? 'text-red-500 bg-red-50' : 'text-slate-500 hover:bg-slate-50'}`} data-testid={`like-btn-${post.post_id}`}>
          <Heart className={`w-4 h-4 ${liked ? 'fill-red-500' : ''}`} /> Like
        </button>
        <button onClick={handleToggleComments} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50" data-testid={`comment-btn-${post.post_id}`}>
          <MessageCircle className="w-4 h-4" /> Comment
        </button>
        <DropdownMenu open={shareOpen} onOpenChange={setShareOpen}>
          <DropdownMenuTrigger asChild>
            <button className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50" data-testid={`share-btn-${post.post_id}`}>
              <Share2 className="w-4 h-4" /> Share
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center">
            <DropdownMenuItem onClick={() => handleShare("facebook")}><Facebook className="w-4 h-4 mr-2 text-blue-600" /> Facebook</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare("twitter")}><Twitter className="w-4 h-4 mr-2 text-sky-500" /> X / Twitter</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare("linkedin")}><Linkedin className="w-4 h-4 mr-2 text-blue-700" /> LinkedIn</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare("copy")}><Copy className="w-4 h-4 mr-2" /> Copy Link</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="px-4 py-3 bg-slate-50/50">
          {loadingComments ? (
            <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-deep-blue mx-auto" /></div>
          ) : (
            <>
              {comments.map(c => (
                <div key={c.comment_id} className="flex gap-2 mb-3" data-testid={`comment-${c.comment_id}`}>
                  <div className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {c.user_name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <div className="bg-white rounded-xl px-3 py-2 flex-1 border border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-xs text-deep-blue">{c.user_name}</span>
                      <span className="text-[10px] text-slate-400">{c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) : ""}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">{c.content}</p>
                  </div>
                </div>
              ))}
              {user && (
                <div className="flex gap-2 mt-2">
                  <div className="w-8 h-8 rounded-full bg-deep-blue flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <div className="flex-1 flex gap-2">
                    <Input
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Write a comment..."
                      className="text-sm h-8"
                      onKeyDown={(e) => e.key === "Enter" && handleComment()}
                      data-testid={`comment-input-${post.post_id}`}
                    />
                    <Button size="sm" className="h-8 bg-deep-blue" onClick={handleComment} data-testid={`comment-send-${post.post_id}`}>
                      <Send className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function CommunityPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [postContent, setPostContent] = useState("");
  const [postFile, setPostFile] = useState(null);
  const [postPreview, setPostPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [filter, setFilter] = useState("all");
  const fileInputRef = useRef(null);

  const fetchPosts = useCallback(async (p = 1) => {
    try {
      if (filter === "following") {
        const res = await axios.get(`${API}/community/following-feed?page=${p}&limit=20`, { withCredentials: true });
        setPosts(res.data.posts || []);
        setTotalPages(res.data.pages || 1);
      } else {
        const params = new URLSearchParams({ page: p, limit: 20 });
        if (filter === "featured") params.set("featured_only", "true");
        if (filter === "blog") params.set("post_type", "blog");
        const res = await axios.get(`${API}/community/feed?${params}`);
        setPosts(res.data.posts || []);
        setTotalPages(res.data.pages || 1);
      }
    } catch { console.error("Failed to fetch feed"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchPosts(page); }, [page, fetchPosts]);

  const handlePost = async () => {
    if (!postContent.trim() && !postFile) { toast.error("Write something or add media"); return; }
    setPosting(true);
    const fd = new FormData();
    fd.append("content", postContent);
    if (postFile) fd.append("file", postFile);
    try {
      await axios.post(`${API}/community/posts`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Posted!");
      setPostContent("");
      setPostFile(null);
      setPostPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchPosts(1);
      setPage(1);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to post"); }
    finally { setPosting(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPostFile(file);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPostPreview({ type: "image", src: ev.target.result });
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("video/")) {
      setPostPreview({ type: "video", name: file.name });
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/community/posts/${postId}`, { withCredentials: true });
      toast.success("Post deleted");
      fetchPosts(page);
    } catch { toast.error("Failed to delete"); }
  };

  const handleFeature = async (postId) => {
    try {
      const res = await axios.put(`${API}/community/posts/${postId}/feature`, {}, { withCredentials: true });
      toast.success(res.data.is_featured ? "Post featured!" : "Post unfeatured");
      fetchPosts(page);
    } catch { toast.error("Failed to update"); }
  };

  const handlePin = async (postId) => {
    try {
      const res = await axios.put(`${API}/community/posts/${postId}/pin`, {}, { withCredentials: true });
      toast.success(res.data.is_pinned ? "Post pinned!" : "Post unpinned");
      fetchPosts(page);
    } catch { toast.error("Failed to update"); }
  };

  const isAdmin = user?.role === "super_admin" || user?.role === "master_admin" || user?.role === "developer" || user?.role === "admin";

  // Sort: pinned first, then by date
  const sortedPosts = [...posts].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return 0;
  });

  return (
    <Layout>
      {/* Hero */}
      <section className="gradient-hero py-12" data-testid="community-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Community</span>
            <h1 className="text-heading-1 text-white mt-3 mb-3">Blue vs Blue Community Hub</h1>
            <p className="text-white/80 text-lg">
              Connect, share stories, and stay engaged with fellow supporters in the fight against prostate cancer.
            </p>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-slate-50">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto space-y-6">

            {/* Stories Bar */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <StoriesBar />
            </div>

            {/* Create Post */}
            {user ? (
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="create-post-box">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-deep-blue flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <div className="flex-1">
                    <Textarea
                      value={postContent}
                      onChange={(e) => setPostContent(e.target.value)}
                      placeholder="Share something with the community..."
                      className="min-h-[80px] border-0 focus-visible:ring-0 resize-none p-0 text-sm"
                      data-testid="post-content-input"
                    />
                  </div>
                </div>

                {postPreview && (
                  <div className="mt-3 relative">
                    {postPreview.type === "image" ? (
                      <img src={postPreview.src} alt="Preview" className="max-h-60 rounded-lg object-contain" />
                    ) : (
                      <div className="bg-slate-100 rounded-lg p-4 flex items-center gap-2">
                        <Video className="w-6 h-6 text-slate-500" />
                        <span className="text-sm text-slate-600">{postPreview.name}</span>
                      </div>
                    )}
                    <button onClick={() => { setPostFile(null); setPostPreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                      className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <div className="flex gap-1">
                    <input ref={fileInputRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleFileSelect} data-testid="post-file-input" />
                    <Button variant="ghost" size="sm" className="text-slate-500 hover:text-awareness-blue" onClick={() => fileInputRef.current?.click()} data-testid="post-add-media">
                      <ImageIcon className="w-4 h-4 mr-1" /> Photo
                    </Button>
                    <Button variant="ghost" size="sm" className="text-slate-500 hover:text-awareness-blue" onClick={() => fileInputRef.current?.click()}>
                      <Video className="w-4 h-4 mr-1" /> Video
                    </Button>
                  </div>
                  <Button onClick={handlePost} disabled={posting || (!postContent.trim() && !postFile)} className="bg-deep-blue text-white hover:bg-deep-blue/90" data-testid="post-submit-btn">
                    {posting ? "Posting..." : "Post"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-3">Join the community to share posts and connect with others</p>
                <div className="flex gap-3 justify-center">
                  <Link to="/login"><Button variant="outline">Log In</Button></Link>
                  <Link to="/register"><Button className="bg-deep-blue text-white">Sign Up</Button></Link>
                </div>
              </div>
            )}

            {/* Filter Tabs */}
            <div className="flex gap-2" data-testid="community-filters">
              {[
                { key: "all", label: "All Posts" },
                { key: "following", label: "Following" },
                { key: "featured", label: "Featured" },
                { key: "blog", label: "Blog Posts" },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => { setFilter(f.key); setPage(1); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    filter === f.key ? 'bg-deep-blue text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Feed */}
            {loading ? (
              <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-deep-blue" /></div>
            ) : sortedPosts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-600">No posts yet</h3>
                <p className="text-slate-400 mt-1">Be the first to share something!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sortedPosts.map(post => (
                  <PostCard
                    key={post.post_id}
                    post={post}
                    user={user}
                    onLike={() => {}}
                    onComment={() => fetchPosts(page)}
                    onDelete={handleDelete}
                    onFeature={handleFeature}
                    onPin={handlePin}
                    onShare={() => {}}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 pt-4">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <span className="px-3 py-1.5 text-sm text-slate-600">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
